import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SettingsProvider } from "@/context/SettingsContext";
import { AuthProvider } from "@/context/AuthContext";
import { captureAttribution } from "@/lib/tracking";
import SiteLayout from "@/components/site/SiteLayout";
import Home from "@/pages/Home";
import HowItWorks from "@/pages/HowItWorks";
import { ProductList, ProductDetail } from "@/pages/Products";
import Book from "@/pages/BookPage";
import FormBuilder from "@/admin/FormBuilder";
import BookConfirmation from "@/pages/BookConfirmation";
import { CardSaved, PaymentResult } from "@/pages/CardPages";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import { AdminLogin, ForgotPassword, ResetPassword } from "@/admin/Auth";
import AdminLayout from "@/admin/AdminLayout";
import Dashboard from "@/admin/Dashboard";
import Customers from "@/admin/Customers";
import CustomerDetail from "@/admin/CustomerDetail";
import Pipeline from "@/admin/Pipeline";
import Bookings from "@/admin/Bookings";
import BookingDetail from "@/admin/BookingDetail";
import ProductsAdmin from "@/admin/ProductsAdmin";
import { Tasks, Payments, Notifications } from "@/admin/Operations";
import { SettingsAdmin, Team, Audit, Privacy } from "@/admin/Admin";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  useEffect(() => { captureAttribution(); }, []);
  return (
    <BrowserRouter>
      <SettingsProvider>
        <AuthProvider>
          <ScrollTop />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/products" element={<ProductList />} />
              <Route path="/products/:id" element={<ProductDetail />} />
              <Route path="/book" element={<Book />} />
              <Route path="/book/confirmation/:ref" element={<BookConfirmation />} />
              <Route path="/book/card-saved" element={<CardSaved />} />
              <Route path="/payment/success" element={<PaymentResult />} />
              <Route path="/payment/cancel" element={<PaymentResult cancelled />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<Legal kind="privacy" />} />
              <Route path="/terms" element={<Legal kind="terms" />} />
              <Route path="/trial-return-policy" element={<Legal kind="trial" />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="bookings/:id" element={<BookingDetail />} />
              <Route path="products" element={<ProductsAdmin />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="payments" element={<Payments />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="settings" element={<SettingsAdmin />} />
              <Route path="forms" element={<FormBuilder />} />
              <Route path="team" element={<Team />} />
              <Route path="audit" element={<Audit />} />
              <Route path="privacy" element={<Privacy />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}

export default App;
